/**
 * Component for displaying the breeding workflow summary
 */

import React, { useEffect } from 'react';
import { DestroyMotherPanel } from './DestroyMotherPanel';
import { useDestroyMother } from '../hooks/useDestroyMother';
import type { WorkflowState, BreedingWorkflowResult } from '../types/breeding';

interface WorkflowSummaryProps {
  state: WorkflowState;
  onStartOver: () => void;
  onUpdateRun: (updates: Partial<BreedingWorkflowResult>) => void;
}

export function WorkflowSummary({ state, onStartOver, onUpdateRun }: WorkflowSummaryProps) {
  const { destroyState, initializeDestroy, handleDestroyYes, handleDestroyNo } = useDestroyMother();

  // Initialize destroy state when summary is shown
  useEffect(() => {
    initializeDestroy();
  }, [initializeDestroy]);

  if (!state.selectedPlant || !state.originalStrain || !state.newStrain) {
    return null;
  }

  const renderNotes = () => {
    if (!state.notes) return null;

    return (
      <div
        className="summary-item"
        style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}
      >
        <strong>Breeding Notes:</strong>

        {state.notes.flavorIntensity && (
          <div className="summary-item" style={{ marginLeft: '16px' }}>
            <strong>Flavor Intensity:</strong>{' '}
            <span>{state.notes.flavorIntensity.charAt(0).toUpperCase() + state.notes.flavorIntensity.slice(1)}</span>
          </div>
        )}

        {state.notes.flavors && state.notes.flavors.length > 0 && (
          <>
            <div className="summary-item" style={{ marginLeft: '16px' }}>
              <strong>Flavors:</strong>
            </div>
            {state.notes.flavors.map((f, idx) => (
              <div key={idx} style={{ marginLeft: '32px', color: '#4a5568' }}>
                {f.category}: {f.descriptors.join(', ')}
              </div>
            ))}
          </>
        )}

        {state.notes.budLeafRatio && (
          <div className="summary-item" style={{ marginLeft: '16px' }}>
            <strong>Bud:Leaf Ratio:</strong> <span>{state.notes.budLeafRatio}</span>
          </div>
        )}

        {state.notes.yield && (
          <div className="summary-item" style={{ marginLeft: '16px' }}>
            <strong>Yield:</strong> <span>{state.notes.yield}</span>
          </div>
        )}

        {state.notes.budStructure && (
          <div className="summary-item" style={{ marginLeft: '16px' }}>
            <strong>Bud Structure:</strong> <span>{state.notes.budStructure}</span>
          </div>
        )}

        {state.notes.potency && (
          <div className="summary-item" style={{ marginLeft: '16px' }}>
            <strong>Potency:</strong> <span>{state.notes.potency}</span>
          </div>
        )}

        {state.notes.additionalNotes && (
          <div className="summary-item" style={{ marginLeft: '16px' }}>
            <strong>Additional Notes:</strong> <span>{state.notes.additionalNotes}</span>
          </div>
        )}
      </div>
    );
  };

  const selectedRoom = state.seedRooms.find((r) => r.id === state.roomId);
  const roomName = selectedRoom ? selectedRoom.name : `Room ${state.roomId}`;
  const isF1Hybrid = state.breedingRunData?.runType === 'seed_production_cross';
  const destroyReason = isF1Hybrid
    ? 'F1 hybrid seed production complete'
    : 'Self-pollination complete';

  return (
    <div className="summary-section">
      <div className="summary-card">
        <h2> Workflow Complete!</h2>
        <div className="summary-content">
          <div className="summary-box" style={{ padding: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#2d3748' }}>Workflow Summary</h3>

            <div className="summary-item">
              <strong>{isF1Hybrid ? 'Seed Parent:' : 'Mother Plant:'}</strong>
              <span>{state.selectedPlant.TSID}</span>
            </div>
            <div className="summary-item">
              <strong>{isF1Hybrid ? 'Seed Parent Strain:' : 'Original Strain:'}</strong>
              <span>
                {state.originalStrain.name} (ID: {state.originalStrain.id})
              </span>
            </div>
            {isF1Hybrid && state.pollenDonorPlant && state.pollenDonorStrain && (
              <>
                <div className="summary-item">
                  <strong>Pollen Donor:</strong>
                  <span>{state.pollenDonorPlant.TSID}</span>
                </div>
                <div className="summary-item">
                  <strong>Pollen Donor Strain:</strong>
                  <span>
                    {state.pollenDonorStrain.name} (ID: {state.pollenDonorStrain.id})
                  </span>
                </div>
              </>
            )}
            <div className="summary-item">
              <strong>{isF1Hybrid ? 'F1 Strain:' : 'New Strain:'}</strong>
              <span>
                {state.newStrain.newName} (ID: {state.newStrain.id})
              </span>
            </div>
            {isF1Hybrid && state.generatedStrainName && state.strainNameOverride && (
              <div className="summary-item">
                <strong>Generated Name:</strong>
                <span>{state.generatedStrainName}</span>
              </div>
            )}
            <div className="summary-item">
              <strong>Seeds Created:</strong>
              <span>{state.seedCount}</span>
            </div>
            <div className="summary-item">
              <strong>Room:</strong>
              <span>{roomName}</span>
            </div>
            <div className="summary-item">
              <strong>Timestamp:</strong>
              <span>{new Date().toLocaleString()}</span>
            </div>

            {renderNotes()}

            <DestroyMotherPanel
              destroyState={destroyState}
              plant={state.selectedPlant}
              destroyReason={destroyReason}
              onDestroyYes={handleDestroyYes}
              onDestroyNo={handleDestroyNo}
              onUpdateRun={onUpdateRun}
            />

            <div
              style={{
                marginTop: '24px',
                padding: '16px',
                background: 'white',
                borderRadius: '6px',
                borderLeft: '4px solid #48bb78',
              }}
            >
              <p style={{ margin: '0', color: '#22543d', fontWeight: 600 }}>
                 Seeds assigned to: <strong>{state.newStrain.newName}</strong>
              </p>
              <p style={{ margin: '8px 0 0 0', color: '#22543d' }}>
                 {isF1Hybrid ? 'Seed parent' : 'Mother'} restored to: <strong>{state.originalStrain.name}</strong>
              </p>
              {isF1Hybrid && state.pollenDonorStrain && (
                <p style={{ margin: '8px 0 0 0', color: '#22543d' }}>
                   Pollen donor recorded as: <strong>{state.pollenDonorStrain.name}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
        <button onClick={onStartOver} className="btn-primary btn-large">
          Start New Workflow
        </button>
      </div>
    </div>
  );
}
